/**
 * OrganizationCTATab Component
 * Manages Call to Action configurations for organizations
 */

import React, { useState, useMemo, useEffect } from "react";
import CtaActivityTypeSelect from "./CtaActivityTypeSelect";
import {
  CTA_ACTIVITY_ANY,
  ctaActivityToColumn,
} from "../../utils/ctaActivityType";
import {
  PlusIcon,
  QrCodeIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import QRCode from "qrcode";
import { useQuery } from "@tanstack/react-query";
import {
  useOrganizationCTAs,
  useCreateOrganizationCTA,
  useOffersForOrganization,
} from "../../hooks/useOrganizationCTAs";
import {
  getDefaultBaseUrl,
  buildOrganizationCTAUrl,
} from "../../services/organizationCTA.service";
import { getLocations } from "../../services/locations.service";
import { SUBSCRIPTION_CTA_BASE_URL } from "../../types/organizationCTA.types";
import type {
  OrganizationCTAType,
  OrganizationCTARewardType,
} from "../../types/organizationCTA.types";
import type { Organization, Location } from "../../types/organization.types";
import LoadingSpinner from "../common/LoadingSpinner";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import OrganizationCTACard from "./OrganizationCTACard";
import PromoCodeSelect from "./PromoCodeSelect";
import PromoCodeLookupStatus from "../common/PromoCodeLookupStatus";

interface OrganizationCTATabProps {
  organization: Organization;
  /** May this user create/edit/delete CTAs (can_manage_organization_ctas)? When false the tab is
   *  read-only: no Add/Edit/Regenerate/Activate/Delete controls (the database would refuse them). */
  canEdit: boolean;
  /** Open the Add CTA form already scoped to this location (from the Locations tab's
   *  "Set a location promo code" link). Consumed once; `onPresetConsumed` clears it. */
  presetLocationId?: string | null;
  onPresetConsumed?: () => void;
}

const OrganizationCTATab: React.FC<OrganizationCTATabProps> = ({
  organization,
  canEdit,
  presetLocationId,
  onPresetConsumed,
}) => {
  const { showToast } = useToast();
  const [showAddCTA, setShowAddCTA] = useState(false);
  const [newCTAName, setNewCTAName] = useState("");
  const [newCTAType, setNewCTAType] = useState<OrganizationCTAType | "">("");

  // Reward configuration
  const [rewardType, setRewardType] =
    useState<OrganizationCTARewardType>("voucher");
  const [selectedOfferId, setSelectedOfferId] = useState<string>("");

  // Voucher configuration (when reward_type === "voucher")
  const [voucherAmount, setVoucherAmount] = useState("");
  const [voucherExpiryDays, setVoucherExpiryDays] = useState("30");
  const [voucherStartDate, setVoucherStartDate] = useState("");
  const [voucherEndDate, setVoucherEndDate] = useState("");

  // URL configuration
  const [includeStateParam, setIncludeStateParam] = useState(true);
  // Off by default: an existing CTA's URL must not change because this option now exists.
  const [useStateDefaults, setUseStateDefaults] = useState(false);
  // Off by default (owner 2026-09-29): an operator must opt a CTA in to singlevoucher=1.
  const [singleVoucher, setSingleVoucher] = useState(false);
  // Default OFF: every QR code already printed carries discountvalue in its URL, and the
  // landing page reads it from there. Defaulting this on would strip the amount from codes
  // already in circulation.
  const [allowVariableVoucherValue, setAllowVariableVoucherValue] =
    useState(false);
  const [useMemberSignup, setUseMemberSignup] = useState(false);
  const [displayAwayPlans, setDisplayAwayPlans] = useState(false);

  // Subscription CTA parameters
  const [ctaParameters, setCtaParameters] = useState<Record<string, string>>(
    {},
  );

  // Other CTA - base URL
  const [otherBaseUrl, setOtherBaseUrl] = useState("");

  // Location selection for affid override
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");

  // MSR EVT-04: the event activity this CTA is for ("" = any activity).
  const [activityType, setActivityType] = useState<string>(CTA_ACTIVITY_ANY);

  // A location handed over by the Locations tab: open the form scoped to it, then let the parent
  // forget it so a later visit to this tab starts clean.
  useEffect(() => {
    if (presetLocationId) {
      // A read-only viewer never opens the form (the Locations link is hidden for them too).
      if (canEdit) {
        setShowAddCTA(true);
        setSelectedLocationId(presetLocationId);
      }
      onPresetConsumed?.();
    }
  }, [presetLocationId, onPresetConsumed, canEdit]);

  // Live QR code preview
  const [previewQRCode, setPreviewQRCode] = useState<string>("");

  // Fetch CTAs
  const {
    data: ctas,
    isLoading,
    error,
    refetch,
  } = useOrganizationCTAs(organization.id);

  // Fetch available offers for this organization
  const { data: availableOffers, isLoading: isLoadingOffers } =
    useOffersForOrganization(organization.id);

  // Create CTA mutation
  const createCTA = useCreateOrganizationCTA();

  // Fetch locations for this organization (for affid selection)
  const { data: locationsData } = useQuery({
    queryKey: ["locations", organization.id],
    queryFn: () => getLocations(organization.id, { limit: 100 }),
  });
  const locations = locationsData?.data || [];

  // Helper to get location state (checks both 'state' and 'address_state' fields)
  const getLocationState = (loc: Location): string => {
    return loc.state || loc.address_state || "";
  };

  // Compute affid and state-related data
  const affidInfo = useMemo(() => {
    const orgAffid = organization.affiliate_id || "";
    const orgState = organization.address_state || "";
    const locationsWithAffid = locations.filter(
      (loc: Location) => loc.affiliate_id && loc.affiliate_id.trim() !== "",
    );
    // Check both 'state' and 'address_state' fields for locations
    const locationsWithState = locations.filter((loc: Location) => {
      const locState = getLocationState(loc);
      return locState.length === 2;
    });
    const hasOrgAffid = orgAffid.trim() !== "";
    const hasOrgState = orgState.length === 2;
    const hasLocationAffids = locationsWithAffid.length > 0;
    const hasLocationStates = locationsWithState.length > 0;

    // Determine if we need to show location selector
    // Show if:
    // 1. Org has no affid but locations do, OR org has affid and locations have different affids
    // 2. Org has no state but locations do (for ste parameter)
    const needsLocationSelectorForAffid =
      (!hasOrgAffid && hasLocationAffids) ||
      (hasOrgAffid &&
        hasLocationAffids &&
        locationsWithAffid.some(
          (loc: Location) => loc.affiliate_id !== orgAffid,
        ));

    const needsLocationSelectorForState = !hasOrgState && hasLocationStates;

    const needsLocationSelector =
      needsLocationSelectorForAffid || needsLocationSelectorForState;

    // Determine the effective affid and state
    let effectiveAffid = orgAffid;
    let effectiveState = orgState;
    if (selectedLocationId) {
      const selectedLoc = locations.find(
        (loc: Location) => loc.id === selectedLocationId,
      );
      if (selectedLoc?.affiliate_id) {
        effectiveAffid = selectedLoc.affiliate_id;
      }
      // Check both 'state' and 'address_state' fields
      const locState = selectedLoc ? getLocationState(selectedLoc) : "";
      if (locState) {
        effectiveState = locState;
      }
    } else if (!hasOrgAffid && locationsWithAffid.length === 1) {
      // Auto-select if only one location has affid and org doesn't
      effectiveAffid = locationsWithAffid[0].affiliate_id || "";
    }

    // Combine locations that have either affid or state for the selector
    const locationsForSelector = locations.filter(
      (loc: Location) =>
        (loc.affiliate_id && loc.affiliate_id.trim() !== "") ||
        getLocationState(loc).length === 2,
    );

    return {
      orgAffid,
      orgState,
      hasOrgAffid,
      hasOrgState,
      locationsWithAffid,
      locationsWithState,
      locationsForSelector,
      hasLocationAffids,
      hasLocationStates,
      needsLocationSelector,
      needsLocationSelectorForAffid,
      needsLocationSelectorForState,
      effectiveAffid,
      effectiveState,
    };
  }, [
    organization.affiliate_id,
    organization.address_state,
    locations,
    selectedLocationId,
  ]);

  // Auto-populate affid parameter when it changes
  useEffect(() => {
    if (newCTAType === "subscription" && affidInfo.effectiveAffid) {
      setCtaParameters((prev) => ({
        ...prev,
        affid: affidInfo.effectiveAffid,
      }));
    }
  }, [newCTAType, affidInfo.effectiveAffid]);

  // Compute preview URL in real-time
  const previewUrl = useMemo(() => {
    if (!newCTAType) {
      return "";
    }

    // Build a mock CTA object to generate the URL
    // Include location state if a location is selected
    const mockCta = {
      id: "preview-id" as const,
      cta_type: newCTAType,
      base_url: newCTAType === "other" ? otherBaseUrl : undefined,
      parameters: ctaParameters,
      voucher_amount:
        rewardType === "voucher" && voucherAmount
          ? parseFloat(voucherAmount)
          : undefined,
      offer:
        rewardType === "offer" && selectedOfferId && availableOffers
          ? availableOffers.find((o) => o.id === selectedOfferId)
          : undefined,
      // Use the effective state (org or selected location) so `ste` matches what
      // "Auto-populated" shows. buildOrganizationCTAUrl also falls back to
      // organization.address_state.
      location: affidInfo.effectiveState
        ? { state: affidInfo.effectiveState }
        : undefined,
      // The three checkboxes MUST flow into the preview so toggling them updates
      // the URL — and the QR, which derives from it — instantly. They were
      // previously omitted, so the checkboxes had no effect during creation.
      include_state_param: includeStateParam,
      use_state_defaults: useStateDefaults,
      single_voucher: singleVoucher,
      use_member_signup: useMemberSignup,
      display_away_plans: displayAwayPlans,
      // Gated exactly like the create payload below: switching to an offer after
      // checking the box must not leave the preview dropping discountvalue.
      allow_variable_voucher_value:
        rewardType === "voucher" && allowVariableVoucherValue,
    };

    return buildOrganizationCTAUrl(mockCta, organization);
  }, [
    newCTAType,
    otherBaseUrl,
    ctaParameters,
    rewardType,
    voucherAmount,
    selectedOfferId,
    availableOffers,
    organization,
    selectedLocationId,
    affidInfo.effectiveState,
    includeStateParam,
    useStateDefaults,
    singleVoucher,
    useMemberSignup,
    displayAwayPlans,
    allowVariableVoucherValue,
  ]);

  // Generate QR code preview when URL changes
  useEffect(() => {
    if (!previewUrl || previewUrl.length < 10) {
      setPreviewQRCode("");
      return;
    }

    const generateQR = async () => {
      try {
        const svgString = await QRCode.toString(previewUrl, {
          type: "svg",
          width: 150,
          margin: 1,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        });
        setPreviewQRCode(svgString);
      } catch {
        setPreviewQRCode("");
      }
    };

    // Debounce QR generation
    const timer = setTimeout(generateQR, 300);
    return () => clearTimeout(timer);
  }, [previewUrl]);

  // Subscription CTA field definitions
  const SUBSCRIPTION_FIELDS = [
    {
      key: "affid",
      label: "Promoter Affiliate ID",
      placeholder: "e.g., ABC123",
    },
    { key: "promo", label: "Promo Code", placeholder: "e.g., SUMMER2025" },
    { key: "altprice", label: "Alt Price", placeholder: "e.g., 10.99" },
    { key: "utm_source", label: "UTM Source", placeholder: "e.g., event" },
    { key: "utm_term", label: "UTM Term", placeholder: "e.g., signup" },
    {
      key: "utm_campaign",
      label: "UTM Campaign",
      placeholder: "e.g., spring_promo",
    },
    { key: "utm_medium", label: "UTM Medium", placeholder: "e.g., qrcode" },
    {
      key: "utm_content",
      label: "UTM Content",
      placeholder: "e.g., banner_ad",
    },
  ];

  // Check if organization (or any of its locations) has required state for subscription CTAs
  // State is required, but can come from org OR a selected location
  const hasRequiredState =
    (organization.address_state && organization.address_state.length === 2) ||
    affidInfo.hasLocationStates;

  const resetForm = () => {
    setShowAddCTA(false);
    setNewCTAName("");
    setNewCTAType("");
    setRewardType("voucher");
    setSelectedOfferId("");
    setVoucherAmount("");
    setVoucherExpiryDays("30");
    setVoucherStartDate("");
    setVoucherEndDate("");
    setCtaParameters({});
    setOtherBaseUrl("");
    setSelectedLocationId("");
    setActivityType(CTA_ACTIVITY_ANY);
    setIncludeStateParam(true);
    setUseStateDefaults(false);
    setSingleVoucher(false);
    setUseMemberSignup(false);
    setDisplayAwayPlans(false);
  };

  // Update parameter value
  const handleParameterChange = (key: string, value: string) => {
    setCtaParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Check facility_id availability for voucher CTAs
  const voucherEligibility = useMemo(() => {
    const orgHasFacilityId = !!organization.facility_id;
    const hasLocations = locations.length > 0;
    const locationsWithFacilityId = locations.filter(
      (loc) => !!loc.facility_id,
    );
    const anyLocationHasFacilityId = locationsWithFacilityId.length > 0;

    // Can create voucher if org OR any location has facility_id
    const canCreateVoucher = orgHasFacilityId || anyLocationHasFacilityId;

    // Show location/org selector when org has locations AND voucher creation is possible
    const needsLocationSelector = hasLocations && canCreateVoucher;

    // Validate the current selection:
    // - empty selectedLocationId = org-level (valid only if org has facility_id)
    // - non-empty selectedLocationId = location-level (valid only if that location has facility_id)
    const hasValidSelection = selectedLocationId
      ? locationsWithFacilityId.some((loc) => loc.id === selectedLocationId)
      : orgHasFacilityId;

    return {
      orgHasFacilityId,
      hasLocations,
      locationsWithFacilityId,
      anyLocationHasFacilityId,
      canCreateVoucher,
      needsLocationSelector,
      hasValidSelection,
    };
  }, [organization.facility_id, locations, selectedLocationId]);

  const handleCreateCTA = async () => {
    if (!newCTAName.trim()) {
      showToast("error", "Please enter a name for the CTA");
      return;
    }

    if (!newCTAType) {
      showToast("error", "Please select a CTA type");
      return;
    }

    // Voucher CTAs require a facility_id (on org or selected location)
    if (rewardType === "voucher") {
      if (!voucherEligibility.canCreateVoucher) {
        showToast(
          "error",
          voucherEligibility.hasLocations
            ? "No locations have a Facility ID. Add a Facility ID to a location or the organization to enable voucher CTAs."
            : "A Facility ID is required before voucher CTAs can be created. Edit the organization to add one.",
        );
        return;
      }
      if (
        voucherEligibility.needsLocationSelector &&
        !voucherEligibility.hasValidSelection
      ) {
        showToast(
          "error",
          "The selected location does not have a Facility ID. Please select a different location or use Organization-level.",
        );
        return;
      }
    }

    // Validate base URL for "other" type
    if (newCTAType === "other" && !otherBaseUrl.trim()) {
      showToast("error", "Please enter a base URL for the CTA");
      return;
    }

    // Validate voucher amount if reward type is voucher
    if (rewardType === "voucher") {
      if (!voucherAmount || parseFloat(voucherAmount) <= 0) {
        showToast("error", "Please enter a valid voucher amount");
        return;
      }
      // Validate that end date is not before start date
      if (
        voucherStartDate &&
        voucherEndDate &&
        voucherEndDate < voucherStartDate
      ) {
        showToast("error", "Valid Until date cannot be before Valid From date");
        return;
      }
    }

    // Validate offer selection if reward type is offer
    if (rewardType === "offer" && !selectedOfferId) {
      showToast("error", "Please select an offer");
      return;
    }

    // Validate location selection for subscription CTAs when org has no state
    if (
      newCTAType === "subscription" &&
      affidInfo.needsLocationSelectorForState &&
      !selectedLocationId
    ) {
      showToast(
        "error",
        "Please select a location. This organization has no state set.",
      );
      return;
    }

    try {
      // Build parameters object
      const finalParameters: Record<string, string> = { ...ctaParameters };

      // Filter out empty values
      Object.keys(finalParameters).forEach((key) => {
        if (!finalParameters[key]?.trim()) {
          delete finalParameters[key];
        }
      });

      await createCTA.mutateAsync({
        organization_id: organization.id,
        location_id: selectedLocationId || undefined,
        name: newCTAName.trim(),
        cta_type: newCTAType,
        base_url:
          newCTAType === "other"
            ? otherBaseUrl.trim()
            : getDefaultBaseUrl(newCTAType),
        parameters: finalParameters,
        reward_type: rewardType,
        offer_id: rewardType === "offer" ? selectedOfferId : undefined,
        voucher_amount:
          rewardType === "voucher" && voucherAmount
            ? parseFloat(voucherAmount)
            : undefined,
        voucher_expiry_days:
          rewardType === "voucher" && voucherExpiryDays
            ? parseInt(voucherExpiryDays, 10)
            : undefined,
        voucher_start_date:
          rewardType === "voucher" && voucherStartDate
            ? voucherStartDate
            : undefined,
        voucher_end_date:
          rewardType === "voucher" && voucherEndDate
            ? voucherEndDate
            : undefined,
        include_state_param:
          newCTAType === "subscription" ? includeStateParam : undefined,
        use_state_defaults:
          newCTAType === "subscription" ? useStateDefaults : undefined,
        single_voucher:
          newCTAType === "subscription" &&
          (rewardType === "voucher" || rewardType === "offer")
            ? singleVoucher
            : undefined,
        use_member_signup:
          newCTAType === "subscription" ? useMemberSignup : undefined,
        display_away_plans:
          newCTAType === "subscription" ? displayAwayPlans : undefined,
        allow_variable_voucher_value:
          newCTAType === "subscription" && rewardType === "voucher"
            ? allowVariableVoucherValue
            : undefined,
        activity_type:
          newCTAType === "subscription"
            ? ctaActivityToColumn(activityType)
            : undefined,
      });

      showToast("success", "Call to Action created");
      resetForm();
      refetch();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create CTA";
      showToast("error", errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold">Error loading CTAs</h3>
        <p className="text-red-600 text-sm mt-1">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Calls to Action
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Create marketing URLs with QR codes for tracking and reward
            distribution
          </p>
        </div>
        {canEdit && !showAddCTA && (
          <Button
            variant="primary"
            size="sm"
            icon={PlusIcon}
            onClick={() => setShowAddCTA(true)}
          >
            Add CTA
          </Button>
        )}
      </div>

      {!canEdit && (
        <p
          className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
          data-testid="cta-read-only-note"
        >
          You can view and copy these Calls to Action. Only admins, managers,
          staff and support admins can add or change them.
        </p>
      )}

      {/* Facility ID Warning */}
      {!voucherEligibility.canCreateVoucher && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">
              Facility ID Required for Voucher CTAs
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {voucherEligibility.hasLocations
                ? "Neither this organization nor any of its locations have a Facility ID configured. Add a Facility ID to enable voucher CTAs."
                : "This organization has no Facility ID configured. Voucher CTAs cannot be created until a Facility ID is added to the organization."}
            </p>
          </div>
        </div>
      )}

      {/* State Warning */}
      {!hasRequiredState && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">
              State Required for Subscription CTAs
            </p>
            <p className="text-sm text-amber-700 mt-1">
              This organization needs a state set before Subscription CTAs can
              generate proper URLs. Edit the organization to set the state.
            </p>
          </div>
        </div>
      )}

      {/* Add CTA Form */}
      {canEdit && showAddCTA && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-4">New Call to Action</h4>

          {/* The location picker below only appears for some CTA types, so say which location this
              CTA is for whenever one is set — otherwise a location chosen from the Locations tab
              would be applied invisibly. */}
          {selectedLocationId && (
            <div
              className="mb-4 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
              data-testid="cta-location-scope"
            >
              <span>
                For location:{" "}
                <strong>
                  {locations.find((l: Location) => l.id === selectedLocationId)
                    ?.name ?? "selected location"}
                </strong>
                . Its promo code applies to events at this location.
              </span>
              <button
                type="button"
                className="ml-3 text-blue-700 underline hover:text-blue-900"
                onClick={() => setSelectedLocationId("")}
              >
                Use organization level instead
              </button>
            </div>
          )}

          <div className="space-y-4">
            {/* CTA Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newCTAName}
                onChange={(e) => setNewCTAName(e.target.value)}
                placeholder="e.g., Main Campaign, Summer Promo, Partner Link"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                A label to identify this CTA (for tracking and reporting)
              </p>
            </div>

            {/* CTA Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setNewCTAType("subscription")}
                  className={`
                    p-4 border rounded-lg text-left transition-colors
                    ${
                      newCTAType === "subscription"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900">Subscription</div>
                  <p className="text-sm text-gray-500 mt-1">
                    Pre-configured for U.S. Law Shield voucher signup
                  </p>
                  <p className="text-xs text-gray-400 mt-2 truncate">
                    {SUBSCRIPTION_CTA_BASE_URL}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setNewCTAType("other")}
                  className={`
                    p-4 border rounded-lg text-left transition-colors
                    ${
                      newCTAType === "other"
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                        : "border-gray-300 hover:border-gray-400"
                    }
                  `}
                >
                  <div className="font-medium text-gray-900">Other</div>
                  <p className="text-sm text-gray-500 mt-1">
                    Custom URL with flexible parameters
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    You define the base URL
                  </p>
                </button>
              </div>
            </div>

            {/* MSR EVT-04: which event activity this CTA is for */}
            {newCTAType === "subscription" && (
              <CtaActivityTypeSelect
                id="new-cta-activity-type"
                value={activityType}
                onChange={setActivityType}
              />
            )}

            {/* Reward Type - only shown after CTA type selected */}
            {newCTAType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reward Type <span className="text-red-500">*</span>
                </label>
                <div className="grid gap-3 grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRewardType("voucher");
                      setSelectedOfferId("");
                    }}
                    className={`
                      p-3 border rounded-lg text-center transition-colors
                      ${
                        rewardType === "voucher"
                          ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                          : "border-gray-300 hover:border-gray-400"
                      }
                    `}
                  >
                    <div className="font-medium text-gray-900 text-sm">
                      Voucher
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Generate discount code
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setRewardType("offer");
                      setVoucherAmount("");
                      setVoucherStartDate("");
                      setVoucherEndDate("");
                    }}
                    className={`
                      p-3 border rounded-lg text-center transition-colors
                      ${
                        rewardType === "offer"
                          ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                          : "border-gray-300 hover:border-gray-400"
                      }
                    `}
                  >
                    <div className="font-medium text-gray-900 text-sm">
                      Offer
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Link to existing offer
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Voucher Configuration (shown when reward type is voucher) */}
            {rewardType === "voucher" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
                <h5 className="text-sm font-medium text-green-800">
                  Voucher Configuration
                </h5>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={voucherAmount}
                        onChange={(e) => setVoucherAmount(e.target.value)}
                        placeholder="20.00"
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration (Days) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={voucherExpiryDays}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\./g, "");
                        setVoucherExpiryDays(val);
                      }}
                      placeholder="30"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Each voucher expires this many days after creation
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid From
                    </label>
                    <input
                      type="date"
                      value={voucherStartDate}
                      onChange={(e) => {
                        setVoucherStartDate(e.target.value);
                        // Clear end date if it's now before the new start date
                        if (voucherEndDate && e.target.value > voucherEndDate) {
                          setVoucherEndDate("");
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid Until
                    </label>
                    <input
                      type="date"
                      value={voucherEndDate}
                      min={voucherStartDate || undefined}
                      onChange={(e) => setVoucherEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <p className="text-xs text-green-700">
                  Vouchers will be generated when users complete the CTA. Each
                  voucher gets a unique code.
                </p>

                {/* Location/org selector for voucher CTAs (orgs with locations) */}
                {voucherEligibility.needsLocationSelector && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-3">
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          Assign To
                        </p>
                        <p className="text-xs text-amber-700 mt-1 mb-2">
                          Choose the organization or a specific location for
                          this voucher CTA.
                        </p>
                        <select
                          value={selectedLocationId}
                          onChange={(e) =>
                            setSelectedLocationId(e.target.value)
                          }
                          className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-white focus:ring-amber-500 focus:border-amber-500"
                        >
                          {voucherEligibility.orgHasFacilityId ? (
                            <option value="">
                              Organization — {organization.name}
                            </option>
                          ) : (
                            <option value="" disabled>
                              Select a location...
                            </option>
                          )}
                          {voucherEligibility.locationsWithFacilityId.map(
                            (loc: Location) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.name}
                                {loc.city ? ` — ${loc.city}` : ""}
                                {getLocationState(loc)
                                  ? `, ${getLocationState(loc)}`
                                  : ""}
                              </option>
                            ),
                          )}
                        </select>
                        {voucherEligibility.hasValidSelection && (
                          <p className="text-xs text-green-700 mt-1">
                            {selectedLocationId
                              ? "Location selected with Facility ID"
                              : `Using organization Facility ID (${organization.facility_id})`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Offer Selection (shown when reward type is offer) */}
            {rewardType === "offer" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <h5 className="text-sm font-medium text-green-800">
                  Select Offer
                </h5>

                {isLoadingOffers ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <LoadingSpinner size="small" />
                    <span>Loading offers...</span>
                  </div>
                ) : availableOffers && availableOffers.length > 0 ? (
                  <div>
                    <select
                      value={selectedOfferId}
                      onChange={(e) => setSelectedOfferId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select an offer...</option>
                      {availableOffers.map((offer) => (
                        <option key={offer.id} value={offer.id}>
                          {offer.name} ({offer.offer_code})
                          {offer.value_amount
                            ? ` - $${offer.value_amount}`
                            : ""}
                        </option>
                      ))}
                    </select>

                    {selectedOfferId && (
                      <div className="mt-2 p-2 bg-white rounded border border-green-200">
                        {(() => {
                          const selected = availableOffers.find(
                            (o) => o.id === selectedOfferId,
                          );
                          if (!selected) {
                            return null;
                          }
                          return (
                            <div className="text-sm">
                              <p className="font-medium text-gray-900">
                                {selected.name}
                              </p>
                              <p className="text-gray-500">
                                Code: {selected.offer_code}
                                {selected.value_amount && (
                                  <span className="ml-2">
                                    Value: ${selected.value_amount}
                                  </span>
                                )}
                              </p>
                              {selected.description && (
                                <p className="text-gray-500 text-xs mt-1">
                                  {selected.description}
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    <p>No offers available for this organization.</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Create an offer in the Offers section first, or set the
                      offer's availability to include this organization.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tracking & Attribution Parameters - for Subscription CTA */}
            {newCTAType === "subscription" && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-gray-800">
                    Tracking & Attribution Parameters
                  </h5>
                  <span className="text-xs text-gray-500">
                    For affiliate tracking and analytics (independent of reward)
                  </span>
                </div>

                {/* Auto-populated fields info */}
                <div className="bg-blue-50 rounded-md p-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">Auto-populated:</p>
                  <ul className="list-disc list-inside text-xs space-y-0.5">
                    <li
                      className={
                        !includeStateParam ? "line-through opacity-50" : ""
                      }
                    >
                      <code className="bg-blue-100 px-1 rounded">ste</code>:{" "}
                      {organization.address_state || "(set in organization)"}
                    </li>
                    <li
                      className={
                        rewardType === "voucher" && allowVariableVoucherValue
                          ? "line-through opacity-50"
                          : ""
                      }
                    >
                      <code className="bg-blue-100 px-1 rounded">
                        discountvalue
                      </code>
                      :{" "}
                      {rewardType === "voucher" && allowVariableVoucherValue
                        ? "(not in the URL \u2014 looked up by cta-voucher-value)"
                        : rewardType === "voucher" && voucherAmount
                          ? voucherAmount
                          : rewardType === "offer" && selectedOfferId
                            ? "(from selected offer)"
                            : "(set voucher amount or select offer)"}
                    </li>
                  </ul>
                </div>

                {/* Include state parameter checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeStateParam}
                    onChange={(e) => setIncludeStateParam(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Include state parameter (
                  <code className="text-xs bg-gray-100 px-1 rounded">ste=</code>
                  ) in URL
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={useStateDefaults}
                    onChange={(e) => setUseStateDefaults(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    Use State Defaults
                    <span className="block text-xs text-gray-500">
                      If the member picks a different state during signup, the
                      voucher amount switches to that state's default. Adds{" "}
                      <code className="bg-gray-100 px-1 rounded">
                        usestatedefaults=1
                      </code>{" "}
                      to the URL. Off by default, so existing CTAs and printed
                      QR codes are unchanged.
                    </span>
                  </span>
                </label>

                {(rewardType === "voucher" || rewardType === "offer") && (
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      aria-label="Single voucher"
                      checked={singleVoucher}
                      onChange={(e) => setSingleVoucher(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Single voucher
                      <span className="block text-xs text-gray-500">
                        Creates the voucher for the primary member only, not the
                        secondary. Adds{" "}
                        <code className="bg-gray-100 px-1 rounded">
                          singlevoucher=1
                        </code>{" "}
                        to the QR code and landing page URL. Off by default.
                      </span>
                    </span>
                  </label>
                )}

                {/* Use member signup URL checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useMemberSignup}
                    onChange={(e) => setUseMemberSignup(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Use member signup URL
                  <span className="text-xs text-gray-500">
                    Skips the voucher landing page and starts on plan selection
                    (unchecked = /voucher/, checked = /member-signup/)
                  </span>
                </label>

                {/* Display away plans checkbox */}
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayAwayPlans}
                    onChange={(e) => setDisplayAwayPlans(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Display away plans
                  <span className="text-xs text-gray-500">
                    (with member signup URL, starts on the Away plans screen;
                    adds plantype=away)
                  </span>
                </label>

                {/* Variable voucher value — only meaningful for voucher CTAs, whose URL is
                    the thing that would otherwise carry the amount. */}
                {rewardType === "voucher" && (
                  <div>
                    <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allowVariableVoucherValue}
                        onChange={(e) =>
                          setAllowVariableVoucherValue(e.target.checked)
                        }
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        Allow this voucher value to change without requiring a
                        new QR Code
                        <span className="block text-xs text-gray-500">
                          Leaves{" "}
                          <code className="bg-gray-100 px-1 rounded">
                            discountvalue
                          </code>{" "}
                          out of the URL so the amount can change without
                          reprinting. The landing page looks up the current
                          amount when the code is scanned.
                        </span>
                      </span>
                    </label>
                    {allowVariableVoucherValue && (
                      <div className="mt-2 ml-6 bg-blue-50 border border-blue-200 rounded-md p-2">
                        <p className="text-xs text-blue-800">
                          The amount is not in the link. When the code is
                          scanned, ww2.uslawshield.com looks up this CTA&rsquo;s
                          current voucher amount, so a change here also applies
                          to QR codes already printed.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Location selector for Affiliate ID and/or State */}
                {affidInfo.needsLocationSelector && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <div className="flex items-start space-x-2">
                      <MapPinIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          Select Location
                          {affidInfo.needsLocationSelectorForState &&
                          affidInfo.needsLocationSelectorForAffid
                            ? " (Required for State & Affiliate ID)"
                            : affidInfo.needsLocationSelectorForState
                              ? " (Required for State)"
                              : " (For Affiliate ID)"}
                        </p>
                        <p className="text-xs text-amber-700 mt-1 mb-2">
                          {affidInfo.needsLocationSelectorForState &&
                          !affidInfo.hasOrgState
                            ? "This organization has no state set. Select a location to use its state for the URL."
                            : affidInfo.hasOrgAffid
                              ? `Organization default: ${affidInfo.orgAffid}. Select a location to override.`
                              : "This organization has no affiliate ID. Select a location with an affiliate ID."}
                        </p>
                        <select
                          value={selectedLocationId}
                          onChange={(e) =>
                            setSelectedLocationId(e.target.value)
                          }
                          className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-white focus:ring-amber-500 focus:border-amber-500"
                        >
                          {affidInfo.hasOrgAffid && affidInfo.hasOrgState && (
                            <option value="">
                              Use organization default ({affidInfo.orgAffid},{" "}
                              {affidInfo.orgState})
                            </option>
                          )}
                          {(!affidInfo.hasOrgAffid ||
                            !affidInfo.hasOrgState) && (
                            <option value="">Select a location...</option>
                          )}
                          {affidInfo.locationsForSelector.map(
                            (loc: Location) => {
                              const locState = getLocationState(loc);
                              return (
                                <option key={loc.id} value={loc.id}>
                                  {loc.name}
                                  {locState ? ` (${locState})` : ""}
                                  {loc.affiliate_id
                                    ? ` - ${loc.affiliate_id}`
                                    : ""}
                                </option>
                              );
                            },
                          )}
                        </select>
                        {selectedLocationId && affidInfo.effectiveState && (
                          <p className="text-xs text-green-700 mt-1">
                            Using state:{" "}
                            <strong>{affidInfo.effectiveState}</strong>
                            {affidInfo.effectiveAffid && (
                              <>
                                , affid:{" "}
                                <strong>{affidInfo.effectiveAffid}</strong>
                              </>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Show effective affid if auto-populated */}
                {affidInfo.effectiveAffid &&
                  !affidInfo.needsLocationSelector && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <p className="text-sm text-green-800">
                        <span className="font-medium">Affiliate ID:</span>{" "}
                        {affidInfo.effectiveAffid}{" "}
                        <span className="text-green-600">
                          (from organization)
                        </span>
                      </p>
                    </div>
                  )}

                {/* Warning if no affid available */}
                {!affidInfo.effectiveAffid &&
                  !affidInfo.needsLocationSelector && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-800">
                        <span className="font-medium">Warning:</span> No
                        affiliate ID configured for this organization or its
                        locations. The affid parameter will need to be entered
                        manually below.
                      </p>
                    </div>
                  )}

                {/* Editable parameters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SUBSCRIPTION_FIELDS.map((field) =>
                    // The promo code is CHOSEN from the organization's codes rather than typed:
                    // 97% of existing CTAs were hand-copying the org's own code.
                    field.key === "promo" ? (
                      <div key={field.key}>
                        <PromoCodeSelect
                          organizationId={organization.id}
                          value={ctaParameters[field.key] || ""}
                          onChange={(code) =>
                            handleParameterChange("promo", code)
                          }
                          helpText="Add more codes on the Promo Codes tab to see them here."
                        />
                        {/* Informational only -- never blocks the save (owner rule 2026-10-25). */}
                        <PromoCodeLookupStatus
                          code={ctaParameters[field.key]}
                          canRecheck={canEdit}
                        />
                      </div>
                    ) : (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={ctaParameters[field.key] || ""}
                          onChange={(e) =>
                            handleParameterChange(field.key, e.target.value)
                          }
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

            {/* URL Parameters Section - for Other CTA */}
            {newCTAType === "other" && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                <h5 className="text-sm font-medium text-gray-800">
                  Custom URL Configuration
                </h5>

                {/* Base URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={otherBaseUrl}
                    onChange={(e) => setOtherBaseUrl(e.target.value)}
                    placeholder="https://example.com/landing-page"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The base URL where users will be directed
                  </p>
                </div>

                {/* Common parameters for other type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Parameters (optional)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        key: "utm_source",
                        label: "UTM Source",
                        placeholder: "e.g., organization",
                      },
                      {
                        key: "utm_medium",
                        label: "UTM Medium",
                        placeholder: "e.g., qrcode",
                      },
                      {
                        key: "utm_campaign",
                        label: "UTM Campaign",
                        placeholder: "e.g., spring_promo",
                      },
                      {
                        key: "utm_term",
                        label: "UTM Term",
                        placeholder: "e.g., signup",
                      },
                    ].map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={ctaParameters[field.key] || ""}
                          onChange={(e) =>
                            handleParameterChange(field.key, e.target.value)
                          }
                          placeholder={field.placeholder}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Live URL & QR Code Preview */}
            {previewUrl && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center space-x-2">
                  <LinkIcon className="h-5 w-5 text-primary-600" />
                  <h5 className="text-sm font-medium text-primary-800">
                    Live Preview
                  </h5>
                </div>

                <div className="flex gap-4">
                  {/* QR Code Preview */}
                  {previewQRCode && (
                    <div className="flex-shrink-0 bg-white p-2 rounded-lg border border-primary-200">
                      <div
                        dangerouslySetInnerHTML={{ __html: previewQRCode }}
                        className="w-[150px] h-[150px]"
                      />
                    </div>
                  )}

                  {/* URL Preview */}
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-primary-700 mb-1">
                      Generated URL
                    </label>
                    <div className="bg-white border border-primary-200 rounded-md p-3 break-all text-xs font-mono text-gray-700 max-h-32 overflow-y-auto">
                      {previewUrl}
                    </div>
                    <p className="mt-2 text-xs text-primary-600">
                      This URL and QR code update live as you fill in the fields
                      above.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col space-y-3 pt-4">
              {/* Requirements helper text */}
              {(!newCTAName.trim() ||
                !newCTAType ||
                (newCTAType === "other" && !otherBaseUrl.trim()) ||
                (rewardType === "voucher" &&
                  (!voucherAmount || parseFloat(voucherAmount) <= 0)) ||
                (rewardType === "voucher" &&
                  voucherEligibility.needsLocationSelector &&
                  !voucherEligibility.hasValidSelection) ||
                (rewardType === "offer" && !selectedOfferId)) && (
                <div className="text-sm text-gray-500 text-right">
                  <span className="font-medium">Required:</span>{" "}
                  {[
                    !newCTAName.trim() && "Name",
                    !newCTAType && "CTA Type",
                    newCTAType === "other" &&
                      !otherBaseUrl.trim() &&
                      "Base URL",
                    rewardType === "voucher" &&
                      (!voucherAmount || parseFloat(voucherAmount) <= 0) &&
                      "Voucher Amount",
                    rewardType === "voucher" &&
                      voucherEligibility.needsLocationSelector &&
                      !voucherEligibility.hasValidSelection &&
                      "Select Location or Organization",
                    rewardType === "offer" &&
                      !selectedOfferId &&
                      "Select Offer",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <Button variant="secondary" onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateCTA}
                  disabled={
                    createCTA.isPending ||
                    !newCTAName.trim() ||
                    !newCTAType ||
                    (newCTAType === "other" && !otherBaseUrl.trim()) ||
                    (rewardType === "voucher" &&
                      (!voucherAmount || parseFloat(voucherAmount) <= 0)) ||
                    (rewardType === "voucher" &&
                      voucherEligibility.needsLocationSelector &&
                      !voucherEligibility.hasValidSelection) ||
                    (rewardType === "offer" && !selectedOfferId)
                  }
                >
                  {createCTA.isPending ? "Creating..." : "Create CTA"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTAs List */}
      {ctas && ctas.length > 0 ? (
        <div className="space-y-4">
          {ctas.map((cta) => (
            <OrganizationCTACard
              key={cta.id}
              cta={cta}
              organization={organization}
              onUpdate={refetch}
              canEdit={canEdit}
            />
          ))}
        </div>
      ) : (
        !showAddCTA && (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <QrCodeIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              No Calls to Action
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              {canEdit
                ? "Create CTAs with QR codes to track marketing campaigns and signups"
                : "This organization has no Calls to Action yet."}
            </p>
            {canEdit && (
              <Button
                variant="primary"
                icon={PlusIcon}
                onClick={() => setShowAddCTA(true)}
              >
                Add First CTA
              </Button>
            )}
          </div>
        )
      )}

      {/* Info Box */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          About Calls to Action
        </h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>
            <strong>Subscription:</strong> Uses the standard U.S. Law Shield
            voucher URL with affiliate tracking
          </li>
          <li>
            <strong>Other:</strong> Create custom URLs with any parameters for
            external landing pages
          </li>
          <li>
            <strong>Voucher:</strong> Generate discount codes with configurable
            amounts and validity dates
          </li>
          <li>
            <strong>Offer:</strong> Link to an existing offer from your offers
            library
          </li>
          <li>Generate QR codes to include in marketing materials</li>
          <li>Create multiple CTAs for A/B testing different campaigns</li>
        </ul>
      </div>
    </div>
  );
};

export default OrganizationCTATab;
